import React, { useMemo } from "react";
import { DossierLabel } from "../dossier/DossierLabel";
import { useTopicEntities, allEntities } from "../../hooks/use-topic-entities";
import { EntityProseLink } from "./EntityConnections";

/**
 * HiddenTruthSection — the canonical "The Hidden Truth" rendering for a dossier.
 *
 * BACKGROUND
 * ----------
 * The previous parser only accepted line-start `## H2` and `### H3` markdown
 * headings. Authors (manual + AI) frequently use other conventions:
 *   - `**Heading**:` inline-bold + body on same line
 *   - `**Heading**` whole-line bold
 *   - `# H1` single hash
 *   - No heading markers at all (single wall paragraph)
 * Eight of 77 published dossiers were rendered as one giant paragraph because
 * of this. See /app/memory/DOSSIER_RENDERING_AUDIT.md for the forensic report.
 *
 * THIS RENDERER
 * -------------
 *  1. Extended parser recognises FIVE heading syntaxes (all backward compatible):
 *       a. `^### Text`                  → h3
 *       b. `^## Text`                   → h2
 *       c. `^# Text`                    → h2  (NEW — single-hash promoted)
 *       d. `^**Text**` (whole-line)     → h2  (NEW)
 *       e. `^**Text**:? body…`          → h2 + first body paragraph split (NEW)
 *  2. Inline `**bold**` and `*italic*` inside body lines are converted to
 *     `<strong>` / `<em>` so they no longer render as literal asterisks.
 *  3. Label canonicalisation maps editorial variants to the user's canonical
 *     5 labels at render-time. DB content is never modified.
 *  4. After parsing, the renderer guarantees ALL FIVE canonical sub-section
 *     headings are present. Any missing one is emitted with an "Editorial
 *     pass pending." placeholder.
 *
 * Backward-compatibility contract:
 *   - The 69 dossiers that already use `##` render identically.
 *   - The 8 broken dossiers (kilners-aura-research et al.) now render with
 *     real `<h2>` elements instead of literal `**Bold**:` text.
 *   - DB schema unchanged; no migration needed.
 */

// The five canonical sub-section headings, in display order.
export const CANONICAL_SUBSECTIONS = [
  "The Mainstream Narrative",
  "Under-Reported Dimensions",
  "Credible Dissenting Voices",
  "Follow the Money",
  "Open Questions",
];

// Maps editorial variants → canonical labels. All matches are case-insensitive,
// run AFTER trimming surrounding punctuation/markdown.
const CANONICAL_MAP = [
  // "Follow the Money"
  { rx: /^follow[\s-]+the[\s-]+(money|power|power\s*dynamics?|power\s*&\s*money)\b.*$/i, label: "Follow the Money" },
  { rx: /^(money[\s-]+trail|the\s+money[\s-]+trail)$/i,                                  label: "Follow the Money" },
  // "Credible Dissenting Voices"
  { rx: /^(credible\s+)?dissenting\s+(voices|analysis|view|perspectives?|opinion).*$/i,  label: "Credible Dissenting Voices" },
  { rx: /^(skeptical|alternative|opposing)\s+(view|voices|analysis).*$/i,                label: "Credible Dissenting Voices" },
  // "Under-Reported Dimensions"
  { rx: /^(what'?s|whats)?\s*been\s*under[\s-]*reported.*$/i,                            label: "Under-Reported Dimensions" },
  { rx: /^under[\s-]*reported(\s+(dimensions?|aspects?|facts?|angles?))?$/i,             label: "Under-Reported Dimensions" },
  { rx: /^less[\s-]+discussed.*$/i,                                                      label: "Under-Reported Dimensions" },
  // "The Mainstream Narrative"
  { rx: /^(the\s+)?mainstream\s+(narrative|story|account|version).*$/i,                  label: "The Mainstream Narrative" },
  { rx: /^official\s+(narrative|story|version).*$/i,                                     label: "The Mainstream Narrative" },
  // "Open Questions"
  { rx: /^open\s+questions?.*$/i,                                                        label: "Open Questions" },
  { rx: /^(unresolved|remaining)\s+questions?.*$/i,                                      label: "Open Questions" },
];

export const canonicaliseHeading = (raw) => {
  if (!raw) return raw;
  const cleaned = raw
    .replace(/^[*#\s]+/, "")    // strip leading asterisks / hashes / whitespace
    .replace(/[*:.\s]+$/, "")   // strip trailing punctuation
    .trim();
  for (const { rx, label } of CANONICAL_MAP) {
    if (rx.test(cleaned)) return label;
  }
  return cleaned;
};

// Inline markdown → React: bold (**…**) and italic (*…*).
// Tiny by intent — we don't want a markdown-library dependency.
// `post` lets callers post-process plain text runs (entity auto-linking).
const renderInline = (text, baseKey, post) => {
  if (!text) return null;
  const proc = post || ((s) => s);
  const out = [];
  let buf = "";
  let i = 0;
  let k = 0;
  const push = (nodes) => (Array.isArray(nodes) ? out.push(...nodes) : out.push(nodes));
  const flush = () => { if (buf) { push(proc(buf, `${baseKey}-t-${k++}`)); buf = ""; } };
  while (i < text.length) {
    // Bold: **word**
    if (text[i] === "*" && text[i + 1] === "*") {
      const end = text.indexOf("**", i + 2);
      if (end > i + 2) {
        flush();
        out.push(<strong key={`${baseKey}-b-${k++}`} className="text-[#E4DFD3] font-semibold">{proc(text.slice(i + 2, end), `${baseKey}-bs-${k}`)}</strong>);
        i = end + 2;
        continue;
      }
    }
    // Italic: *word* (avoid matching ** by checking previous char)
    if (text[i] === "*" && text[i + 1] !== "*" && (i === 0 || text[i - 1] !== "*")) {
      const end = text.indexOf("*", i + 1);
      if (end > i + 1 && text[end + 1] !== "*") {
        flush();
        out.push(<em key={`${baseKey}-i-${k++}`} className="italic text-[#E4DFD3]/95">{proc(text.slice(i + 1, end), `${baseKey}-is-${k}`)}</em>);
        i = end + 1;
        continue;
      }
    }
    buf += text[i++];
  }
  flush();
  return out;
};

// ---------------------------------------------------------------------------
// Entity auto-linking — every Archive entity mention becomes a doorway.
// ---------------------------------------------------------------------------
const buildEntityMatcher = (ents) => {
  if (!ents?.length) return null;
  const byName = {};
  const names = [];
  for (const e of ents) {
    if (!e.name || e.name.length < 4) continue;
    byName[e.name] = e;
    names.push(e.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  }
  if (!names.length) return null;
  names.sort((a, b) => b.length - a.length);
  return { rx: new RegExp(`(${names.join("|")})`, "g"), byName };
};

const makeLinkifier = (matcher) => {
  if (!matcher) return null;
  return (str, keyBase) => {
    const out = [];
    let last = 0, m, k = 0;
    matcher.rx.lastIndex = 0;
    while ((m = matcher.rx.exec(str))) {
      const before = str[m.index - 1], after = str[m.index + m[0].length];
      if ((before && /[A-Za-z0-9]/.test(before)) || (after && /[A-Za-z0-9]/.test(after))) continue;
      if (m.index > last) out.push(str.slice(last, m.index));
      out.push(
        <EntityProseLink key={`${keyBase}-e${k++}`} e={matcher.byName[m[0]]}>{m[0]}</EntityProseLink>
      );
      last = m.index + m[0].length;
    }
    if (!out.length) return str;
    if (last < str.length) out.push(str.slice(last));
    return out;
  };
};

// ---------------------------------------------------------------------------
// CASE SNAPSHOT — structured intelligence-briefing replacement for the old
// "Key Facts" paragraph. Extracted from `## Key Facts` bullets; the section
// is removed from the prose flow and re-rendered as labelled blocks.
// ---------------------------------------------------------------------------
export const extractKeyFacts = (text) => {
  if (!text) return { facts: null, body: text };
  const rx = /\n?##\s*Key Facts\s*\n([\s\S]*?)(?=\n##\s|$)/i;
  const m = text.match(rx);
  if (!m) return { facts: null, body: text };
  const facts = [];
  for (const line of m[1].split("\n")) {
    const b = line.match(/^\s*[-•*]\s*\*{0,2}([^:*]{2,60}?)\*{0,2}\s*:\s*(.+)$/);
    if (!b) continue;
    const label = b[1].trim();
    const value = b[2].replace(/\*\*/g, "").trim();
    let items;
    if (value.includes(";")) items = value.split(";");
    else if ((value.match(/,/g) || []).length >= 2) items = value.split(",");
    else items = [value];
    facts.push({ label, items: items.map((s) => s.trim()).filter(Boolean) });
  }
  if (!facts.length) return { facts: null, body: text };
  return { facts, body: text.replace(rx, "\n\n[[CASE_SNAPSHOT]]\n\n") };
};

const CaseSnapshot = ({ facts, render }) => (
  <div className="my-8 border p-5 md:p-6" style={{ borderColor: "#2D2A26", background: "#0F0E12" }}
    data-testid="case-snapshot">
    <div className="flex items-center gap-3 mb-6">
      <span className="h-px w-8 shrink-0" style={{ background: "#D92D20" }} />
      <span className="font-mono text-[10px] uppercase tracking-[0.5em] text-[#D92D20]">Case Snapshot</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6">
      {facts.map((f, i) => (
        <div key={i} data-testid={`snapshot-block-${i}`}>
          <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A68F5E] mb-1.5">{f.label}</div>
          {f.items.map((it, j) => (
            <div key={j} className="text-[13.5px] text-[#E4DFD3]/90 leading-snug flex gap-2">
              {f.items.length > 1 && <span className="text-[#454038] shrink-0">·</span>}
              <span>{render(it, `sn-${i}-${j}`)}</span>
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/**
 * Tokenise the raw `hidden_truth` string into a sequence of blocks:
 *   { kind: "h2" | "h3" | "p", text, canonical? }
 * No React yet — keeps the function testable.
 */
export const tokeniseDossierBody = (text) => {
  if (!text) return [];
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks = [];
  let paraBuf = [];

  const flushPara = () => {
    if (!paraBuf.length) return;
    const joined = paraBuf.join(" ").trim();
    if (joined) blocks.push({ kind: "p", text: joined });
    paraBuf = [];
  };

  const pushHeading = (text, level = "h2") => {
    flushPara();
    const canonical = canonicaliseHeading(text);
    blocks.push({ kind: level, text: canonical, raw: text });
  };

  for (const rawLine of lines) {
    const line = rawLine.trimEnd();

    // (a) H3 markdown
    let m = line.match(/^###\s+(.+?)\s*$/);
    if (m) { pushHeading(m[1], "h3"); continue; }

    // (b) H2 markdown
    m = line.match(/^##\s+(.+?)\s*$/);
    if (m) { pushHeading(m[1], "h2"); continue; }

    // (c) Single-hash promoted to h2 (NEW)
    m = line.match(/^#\s+(.+?)\s*$/);
    if (m) { pushHeading(m[1], "h2"); continue; }

    // (d) Whole-line bold heading (NEW)  →  `**Heading**` or `**Heading**:`
    m = line.match(/^\*\*([^*\n]{2,80})\*\*\s*[:.\-—]?\s*$/);
    if (m) { pushHeading(m[1], "h2"); continue; }

    // (e) Inline bold heading + body (NEW)  →  `**Heading**: body text…`
    m = line.match(/^\*\*([^*\n]{2,80})\*\*\s*[:.\-—]\s+(.+)$/);
    if (m) {
      pushHeading(m[1], "h2");
      paraBuf.push(m[2]);
      continue;
    }

    // Blank line — paragraph break
    if (!line.trim()) { flushPara(); continue; }

    // Body line — accumulate
    paraBuf.push(line);
  }
  flushPara();
  return blocks;
};

/**
 * Render the parsed blocks into React. Inline ** and * conversion happens
 * here so the renderer can be tested independently of the tokeniser.
 */
const blocksToReact = (blocks) => {
  return blocks.map((b, i) => {
    if (b.kind === "h3") {
      return (
        <DossierLabel key={`h3-${i}`} as="h3" tone="muted" className="mt-8 mb-2 text-[10px]">
          {b.text}
        </DossierLabel>
      );
    }
    if (b.kind === "h2") {
      return (
        <DossierLabel key={`h2-${i}`} as="h2">
          {b.text}
        </DossierLabel>
      );
    }
    return (
      <p key={`p-${i}`} className="text-[15px] leading-relaxed text-[#E4DFD3]/90">
        {renderInline(b.text, `p${i}`)}
      </p>
    );
  });
};

/**
 * Guarantee all 5 canonical sub-section headings are present.
 * If any are missing, append placeholder blocks at the end of the section so
 * the dossier ALWAYS exposes the full editorial framework.
 */
export const ensureCanonicalCoverage = (blocks) => {
  const seen = new Set();
  for (const b of blocks) {
    if (b.kind === "h2") seen.add(b.text);
  }
  const missing = CANONICAL_SUBSECTIONS.filter((s) => !seen.has(s));
  if (missing.length === 0) return blocks;
  const tail = [];
  for (const label of missing) {
    tail.push({ kind: "h2", text: label, raw: label });
    tail.push({ kind: "p", text: "Editorial pass pending.", placeholder: true });
  }
  return [...blocks, ...tail];
};

export const HiddenTruthSection = ({ text, slug }) => {
  const entityGroups = useTopicEntities(slug);
  const linkify = useMemo(
    () => makeLinkifier(buildEntityMatcher(allEntities(entityGroups))),
    [entityGroups],
  );
  const { facts, body } = useMemo(() => extractKeyFacts(text), [text]);

  let blocks = tokeniseDossierBody(body);
  // Canonical-coverage placeholders only apply to legacy dossiers that use
  // the 5-heading framework; Standard-v2 dossiers have their own structure.
  const canonicalSeen = blocks.filter(
    (b) => b.kind === "h2" && CANONICAL_SUBSECTIONS.includes(b.text)).length;
  if (canonicalSeen >= 2) blocks = ensureCanonicalCoverage(blocks);

  const rendered = blocks.map((b, i) => {
    if (b.kind === "h3") {
      return (
        <DossierLabel key={`h3-${i}`} as="h3" tone="muted" className="mt-8 mb-2 text-[10px]" data-canonical={b.text}>
          {b.text}
        </DossierLabel>
      );
    }
    if (b.kind === "h2") {
      // The outer Executive Summary section already renders the dossier
      // summary — suppress the duplicate in-body heading (body remains).
      if (/^executive summary$/i.test(b.text)) return null;
      return (
        <DossierLabel
          key={`h2-${i}`}
          as="h2"
          data-canonical={b.text}
          data-testid={
            CANONICAL_SUBSECTIONS.includes(b.text)
              ? `hidden-truth-h2-${b.text.toLowerCase().replace(/[^a-z]+/g, "-")}`
              : undefined
          }
        >
          {b.text}
        </DossierLabel>
      );
    }
    if (b.text.trim() === "[[CASE_SNAPSHOT]]") {
      return facts ? (
        <CaseSnapshot key={`cs-${i}`} facts={facts}
          render={(s, kk) => renderInline(s, kk, linkify)} />
      ) : null;
    }
    return (
      <p
        key={`p-${i}`}
        className={
          "text-[15px] leading-relaxed " +
          (b.placeholder ? "text-[#757065] italic" : "text-[#E4DFD3]/90")
        }
        data-placeholder={b.placeholder ? "true" : undefined}
      >
        {renderInline(b.text, `p${i}`, linkify)}
      </p>
    );
  });

  return (
    <section className="mt-12" data-testid="hidden-truth-section">
      <DossierLabel as="h2" className="mt-0">
        The Hidden Truth
      </DossierLabel>
      <h3 className="font-heading text-3xl text-[#E4DFD3] mb-6">
        What the headlines won&apos;t tell you
      </h3>
      <div className="prose prose-invert max-w-none font-body text-[#E4DFD3] leading-relaxed space-y-4">
        {rendered}
      </div>
    </section>
  );
};

// Re-export helpers (renderDossierBody preserved for any external import).
export const renderDossierBody = (text) => blocksToReact(tokeniseDossierBody(text));

export default HiddenTruthSection;
